import type { DailyTaskRewardDto } from "../../../api/dto/daily_tasks.dto.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { RewardWidgetsService } from "../../../reward_widgets/reward_widgets_service.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { LevelBoxOpenOverlay } from "./level_box_open_overlay.js";

export interface FirstStepsChestOverlayParams
{
  backdrop?: UIElement;
  preview_rewards?: DailyTaskRewardDto[];
  on_claimed?: () => void;
}

export class FirstStepsChestOverlay extends LevelBoxOpenOverlay
{
  private static readonly LOGO_W = 430;
  private static readonly LOGO_H = 358;
  private static readonly LOGO_Y = 60;
  private static readonly TITLE_Y = 440;
  private static readonly TITLE_H = 160;
  private static readonly TITLE_FONT = 60;
  private static readonly TITLE_OUTLINE = 10;
  private static readonly BOX_H = 347;
  private static readonly BOX_CENTER_Y = 1000;
  private static readonly OPENED_BOX_CENTER_Y = 620;
  private static readonly TITLE_HIDE_MS = 120;
  private static readonly HINT_Y = 1570;
  private static readonly REWARD_COLUMNS = 3;

  private claimed = false;
  private title_text?: UITextElement;

  private get chestParams(): FirstStepsChestOverlayParams
  {
    return ( this.params ?? {} ) as FirstStepsChestOverlayParams;
  }

  private createLogo(): void
  {
    const w = LayoutScaler.widthFromDesign( FirstStepsChestOverlay.LOGO_W );
    const h = LayoutScaler.heightFromDesign( FirstStepsChestOverlay.LOGO_H );
    const x = this.rect.getW2() - w / 2;
    const y = LayoutScaler.yFromDesign( FirstStepsChestOverlay.LOGO_Y );

    const logo = new UIImage( this.ctx, x, y, w, h, ImgID.UI_FirstStepsLogo );
    logo.setClickable( false );
    this.addChild( logo );
  }

  protected createTitle(): void
  {
    this.createLogo();

    const bounds = this.titleBounds();
    const y = LayoutScaler.yFromDesign( FirstStepsChestOverlay.TITLE_Y );
    const h = LayoutScaler.heightFromDesign( FirstStepsChestOverlay.TITLE_H );

    const title = new UITextElement( this.ctx, bounds.x, y, bounds.w, h, {
      localization_key: "FIRST_STEPS.CHEST_TITLE",
      font_size: LayoutScaler.textSizeFromDesign( FirstStepsChestOverlay.TITLE_FONT ),
      font_weight: 900,
      text_style: TextStyleScheme.rubik,
      text_color: ColorScheme.white,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      outline_color: ColorScheme.first_steps_subtitle_outline,
      outline_width: LayoutScaler.textBorderSizeFromDesign( FirstStepsChestOverlay.TITLE_OUTLINE ),
      color_map: { yellow: ColorScheme.first_steps_subtitle_highlight },
      auto_wrap: false,
      scale_to_width: true,
    } );
    title.setClickable( false );
    this.addChild( title );
    this.title_text = title;
  }

  protected closedBoxId(): ImgID
  {
    return ImgID.UI_FirstStepsChestClosed;
  }

  protected openBoxId(): ImgID
  {
    return ImgID.UI_FirstStepsChestOpen;
  }

  protected boxDesignH(): number
  {
    return FirstStepsChestOverlay.BOX_H;
  }

  protected boxCenterY(): number
  {
    return FirstStepsChestOverlay.BOX_CENTER_Y;
  }

  protected openedBoxCenterY(): number
  {
    return FirstStepsChestOverlay.OPENED_BOX_CENTER_Y;
  }

  protected hintY(): number
  {
    return FirstStepsChestOverlay.HINT_Y;
  }

  protected rewardColumns(): number
  {
    return FirstStepsChestOverlay.REWARD_COLUMNS;
  }

  protected createLevelLabel(): void {}

  protected async openRewards(): Promise<DailyTaskRewardDto[]>
  {
    this.title_text?.hideElement( FirstStepsChestOverlay.TITLE_HIDE_MS );

    const preview = this.chestParams.preview_rewards;
    if ( preview )
      return preview;

    return ( await RewardWidgetsService.claimFirstSteps() ).rewards;
  }

  protected applyClaimedRewards(): void
  {
    this.claimed = true;
    if ( !this.chestParams.preview_rewards )
      RewardWidgetsService.applyClaimedRewards();
  }

  public override onOverlayWillClose(): void
  {
    super.onOverlayWillClose();
    if ( this.claimed )
      this.chestParams.on_claimed?.();
    else
      this.chestParams.backdrop?.invalidateSubtree();
  }

  private drawDim(): void
  {
    const ctx = this.ctx;
    ctx.save();
    ctx.setTransform( 1, 0, 0, 1, 0, 0 );
    ctx.fillStyle = ColorScheme.first_steps_chest_dim;
    ctx.fillRect( 0, 0, ctx.canvas.width, ctx.canvas.height );
    ctx.restore();
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    const backdrop = this.chestParams.backdrop;
    if ( backdrop )
    {
      backdrop.invalidateSubtree();
      backdrop.render();
      this.drawDim();
    }

    super.render();
  }

  public name(): string
  {
    return "FirstStepsChestOverlay";
  }
}
